/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Student Registration Page - With age verification and guardian approval flow
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Mail, User, Loader2, CheckCircle, AlertCircle, Calendar, Send } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '../stores/authStore'
import { getErrorMessage } from '../utils/errorUtils.js'
import { handleEmailVerificationRequired } from '../utils/registrationUtils'
import AuthLayout from '../components/auth/AuthLayout'
import MarketingPanel from '../components/auth/MarketingPanel'
import FormInput from '../components/auth/FormInput'
import PasswordInput from '../components/auth/PasswordInput'
import SocialAuthButtons from '../components/auth/SocialAuthButtons'
import { createLogger } from '../utils/logger'

const logger = createLogger('RegisterStudent')

const RegisterStudent = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    guardianEmail: '',
    ageConfirmed: false,
    termsAccepted: false,
    privacyAccepted: false
  })
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [invitationData, setInvitationData] = useState(null)
  const [loadingInvitation, setLoadingInvitation] = useState(false)
  const [invitationToken, setInvitationToken] = useState(null)
  const [isMinor, setIsMinor] = useState(false)
  const [isUnder13, setIsUnder13] = useState(false)
  const [isTeen, setIsTeen] = useState(false) // 13-17 years old
  const [skipGuardian, setSkipGuardian] = useState(false) // Teen opted out of guardian
  const [showMinorFlow, setShowMinorFlow] = useState(false)
  const [approvalRequestSent, setApprovalRequestSent] = useState(false)

  const { register, setSession, checkAuth } = useAuthStore()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Get referral code from URL (if present)
  const referralCode = searchParams.get('ref')

  // Pre-fill email from query parameter (used by email invite flow)
  useEffect(() => {
    const emailFromUrl = searchParams.get('email')
    if (emailFromUrl) {
      setFormData((prev) => ({
        ...prev,
        email: emailFromUrl
      }))
    }
  }, [searchParams])

  // Check for invitation token in URL
  useEffect(() => {
    const token = searchParams.get('token')
    if (token) {
      setInvitationToken(token)
      loadInvitationData(token)
    }
  }, [searchParams])

  const loadInvitationData = async (token) => {
    setLoadingInvitation(true)
    try {
      const response = await fetch(`/api/invitations/${token}`)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || 'Invalid invitation')
      }

      const data = await response.json()

      if (data.success && data.data) {
        setInvitationData(data.data)
        // Only pre-fill guardian email if invitation is from a family organization (inviter is guardian)
        // For admin invitations (organizationId=null), user should enter their own guardian email
        const isFromFamilyGuardian =
          data.data.organization?.type === 'family' && data.data.invitedBy?.email
        // Pre-fill email, firstName, lastName, and guardianEmail from invitation
        setFormData((prev) => ({
          ...prev,
          email: data.data.email,
          firstName: data.data.metadata?.firstName || prev.firstName,
          lastName: data.data.metadata?.lastName || prev.lastName,
          guardianEmail: isFromFamilyGuardian ? data.data.invitedBy.email : prev.guardianEmail
        }))
      } else {
        toast.error('Invalid invitation')
      }
    } catch (error) {
      logger.error('Error loading invitation:', error)
      toast.error(error.message || 'Failed to load invitation')
    } finally {
      setLoadingInvitation(false)
    }
  }

  const calculateAge = (birthDate) => {
    const today = new Date()
    const birth = new Date(birthDate)
    let age = today.getFullYear() - birth.getFullYear()
    const monthDiff = today.getMonth() - birth.getMonth()

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--
    }

    return age
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }))

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }))
    }

    // Check age when date of birth changes - differentiate between under 13 and 13-17
    if (name === 'dateOfBirth' && value) {
      const age = calculateAge(value)
      const coppaAgeThreshold = 13 // Under 13 requires mandatory guardian
      const minorAgeThreshold = 18 // 18+ is adult

      if (age < coppaAgeThreshold) {
        // Under 13: mandatory guardian approval required
        setIsUnder13(true)
        setIsTeen(false)
        setIsMinor(true)
        setShowMinorFlow(true)
        setSkipGuardian(false) // Reset skip guardian for under 13
      } else if (age < minorAgeThreshold) {
        // 13-17: guardian is optional
        setIsUnder13(false)
        setIsTeen(true)
        setIsMinor(true)
        setShowMinorFlow(true)
      } else {
        // 18+: no guardian needed
        setIsUnder13(false)
        setIsTeen(false)
        setIsMinor(false)
        setShowMinorFlow(false)
        setSkipGuardian(false)
      }
    }

    // Real-time password match validation
    if (name === 'confirmPassword' || (name === 'password' && formData.confirmPassword)) {
      if (name === 'confirmPassword') {
        if (value && value !== formData.password) {
          setErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match' }))
        } else {
          setErrors((prev) => ({ ...prev, confirmPassword: '' }))
        }
      } else if (name === 'password') {
        if (formData.confirmPassword && value !== formData.confirmPassword) {
          setErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match' }))
        } else {
          setErrors((prev) => ({ ...prev, confirmPassword: '' }))
        }
      }
    }
  }

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: checked
    }))

    // Clear error for this field when user checks
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required'
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required'
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    // If there's an invitation, email must match
    if (invitationData && formData.email.toLowerCase() !== invitationData.email.toLowerCase()) {
      newErrors.email = 'Email must match the invitation email'
    }

    // Date of birth validation
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required'
    } else {
      const birthDate = new Date(formData.dateOfBirth)
      const today = new Date()

      if (birthDate >= today) {
        newErrors.dateOfBirth = 'Please enter a valid date of birth'
      }

      const age = calculateAge(formData.dateOfBirth)
      if (age > 120) {
        newErrors.dateOfBirth = 'Please enter a valid date of birth'
      }
    }

    // Minor-specific validation (skip only if invitation is from a family guardian)
    const isFromFamilyGuardian =
      invitationData?.organization?.type === 'family' && invitationData?.invitedBy?.email

    // Under 13: guardian email is mandatory
    // 13-17 (teen): guardian email is optional unless they haven't opted out
    if (isUnder13 && !isFromFamilyGuardian) {
      // Under 13: guardian email is required
      if (!formData.guardianEmail.trim()) {
        newErrors.guardianEmail = 'Guardian email is required for students under 13'
      } else if (!emailRegex.test(formData.guardianEmail)) {
        newErrors.guardianEmail = 'Please enter a valid guardian email address'
      } else if (formData.guardianEmail.toLowerCase() === formData.email.toLowerCase()) {
        newErrors.guardianEmail = 'Guardian email must be different from your email'
      }
    } else if (isTeen && !isFromFamilyGuardian && !skipGuardian) {
      // 13-17 with checkbox unchecked: guardian email is required
      if (!formData.guardianEmail.trim()) {
        newErrors.guardianEmail = 'Guardian email is required, or check the box above to skip'
      } else if (!emailRegex.test(formData.guardianEmail)) {
        newErrors.guardianEmail = 'Please enter a valid guardian email address'
      } else if (formData.guardianEmail.toLowerCase() === formData.email.toLowerCase()) {
        newErrors.guardianEmail = 'Guardian email must be different from your email'
      }
    }

    // Password validation (not required for minors as they won't be activated yet)
    if (!isMinor || invitationToken) {
      if (!formData.password) {
        newErrors.password = 'Password is required'
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters'
      }

      // Confirm password validation
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password'
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match'
      }
    }

    // Age confirmation validation (required for everyone)
    if (!formData.ageConfirmed) {
      newErrors.ageConfirmed = 'You must confirm you are 13 years of age or older'
    }

    // Terms and Privacy validation (required for everyone)
    if (!formData.termsAccepted) {
      newErrors.termsAccepted = 'You must accept the Terms of Service'
    }

    if (!formData.privacyAccepted) {
      newErrors.privacyAccepted = 'You must accept the Privacy Policy'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error('Please fix the errors in the form')
      return
    }

    setIsLoading(true)

    try {
      // Only skip guardian approval if invited by a family guardian (they already know it's their child)
      // Admin/system-wide invitations still require guardian approval for minors
      const isFromFamilyGuardian =
        invitationData?.organization?.type === 'family' && invitationData?.invitedBy?.email

      // Under 13 and NOT invited by family guardian: mandatory guardian approval
      if (isUnder13 && !isFromFamilyGuardian) {
        const response = await fetch('/api/guardian-approval/register-minor', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            dateOfBirth: formData.dateOfBirth,
            guardianEmail: formData.guardianEmail,
            password: formData.password,
            invitationToken: invitationToken,
            referralCode: referralCode || undefined
          })
        })

        const data = await response
          .json()
          .catch(() => ({ success: false, message: 'Invalid server response' }))

        if (response.ok && data.success) {
          // Check if this is a pre-approved registration (guardian invitation)
          // In that case, the backend returns a session for auto-login
          if (data.session?.token) {
            toast.success(data.message || 'Account created successfully!')
            await setSession(data.user, data.session?.token)
            navigate('/dashboard')
          } else {
            // Standard flow: approval request sent, show confirmation
            setApprovalRequestSent(true)
            toast.success(`Approval request sent to ${formData.guardianEmail}`)
          }
        } else {
          throw new Error(data.message || 'Failed to send approval request')
        }
      } else if (isTeen && !isFromFamilyGuardian) {
        // 13-17: Teen registration with optional guardian
        const response = await fetch('/api/guardian-approval/register-teen', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            dateOfBirth: formData.dateOfBirth,
            guardianEmail: skipGuardian ? null : formData.guardianEmail || null,
            password: formData.password,
            invitationToken: invitationToken,
            referralCode: referralCode || undefined
          })
        })

        const data = await response
          .json()
          .catch(() => ({ success: false, message: 'Invalid server response' }))

        if (response.ok && data.success) {
          // Teen registration successful - they get logged in automatically
          if (data.guardianInvitationSent) {
            toast.success(`Account created! Invitation sent to ${formData.guardianEmail}`)
          } else {
            toast.success('Account created! You can invite a guardian later from your profile.')
          }

          // Set user and session in auth store for immediate login
          await setSession(data.user, data.session?.token)
          navigate('/dashboard')
        } else {
          throw new Error(data.message || 'Failed to create account')
        }
      } else {
        // Adult student OR minor invited by family guardian - proceed with normal registration
        // Destructure to exclude confirmPassword and guardianEmail from registerData
        const {
          confirmPassword: _confirmPassword,
          guardianEmail: _guardianEmail,
          ...registerData
        } = formData
        registerData.accountType = 'student'

        // Include invitation token if present
        if (invitationToken) {
          registerData.invitationToken = invitationToken
        }

        // Include referral code if present
        logger.debug('[RegisterStudent] referralCode from URL:', referralCode)
        if (referralCode) {
          registerData.referralCode = referralCode
        }
        logger.debug('[RegisterStudent] registerData being sent:', registerData)

        const result = await register(registerData)

        if (result.success) {
          // Check if email verification is required
          if (handleEmailVerificationRequired(result, formData.email, navigate)) {
            return
          }

          toast.success('Welcome to Deaveris!')

          if (invitationToken && invitationData) {
            toast.success(
              `Joined ${invitationData.organization?.name || 'organization'} successfully!`
            )
          }

          // Refresh session to ensure user data is fully loaded before navigation
          await checkAuth()
          navigate('/dashboard')
        } else {
          throw new Error(getErrorMessage(result.error, 'Registration failed'))
        }
      }
    } catch (error) {
      logger.error('Registration error:', error)
      toast.error(error.message || 'Registration failed. Please try again.')

      // Clear password fields on error
      setFormData((prev) => ({
        ...prev,
        password: '',
        confirmPassword: ''
      }))

      setTimeout(() => {
        document.getElementById('password')?.focus()
      }, 100)
    } finally {
      setIsLoading(false)
    }
  }

  // Show success message if approval request sent
  if (approvalRequestSent) {
    return (
      <AuthLayout
        marketingContent={<MarketingPanel />}
        formTitle="Approval Request Sent"
        formSubtitle="Check your guardian's email"
      >
        <div className="space-y-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-success-50 border border-success-200 rounded-xl p-6 text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-success-100 rounded-full mb-4">
              <Send className="w-8 h-8 text-success-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Request Sent Successfully!
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We've sent an approval request to <strong>{formData.guardianEmail}</strong>
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Your guardian will receive an email with instructions to create their account and
              approve your registration.
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mt-2">
              Once approved, we'll send you an email letting you know you're ready to sign in.
            </p>
          </motion.div>

          <div className="text-center">
            <Link
              to="/login"
              className="text-sm font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
            >
              Return to login
            </Link>
          </div>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      marketingContent={<MarketingPanel />}
      formTitle="Create Student Account"
      formSubtitle={
        invitationData
          ? `Join ${invitationData.organization?.name || 'Deaveris'}`
          : 'Start your SAT prep journey'
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Invitation Status Banner */}
        <AnimatePresence>
          {loadingInvitation && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-primary-50 border border-primary-200 rounded-xl p-4 flex items-center gap-3"
            >
              <Loader2 className="w-5 h-5 text-primary-600 animate-spin" />
              <span className="text-sm text-primary-700">Loading invitation...</span>
            </motion.div>
          )}

          {invitationData && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-success-50 border border-success-200 rounded-xl p-4 space-y-2"
            >
              <div className="flex items-center gap-2 text-success-700">
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm font-semibold">Invitation verified</span>
              </div>
              <p className="text-sm text-success-600 pl-7">Email: {invitationData.email}</p>
              {invitationData.organization && (
                <p className="text-sm text-success-600 pl-7">
                  Organization: {invitationData.organization.name}
                </p>
              )}
            </motion.div>
          )}

          {/* Under 13 Warning Banner - mandatory guardian approval */}
          {isUnder13 &&
            !(
              invitationData?.organization?.type === 'family' && invitationData?.invitedBy?.email
            ) && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-amber-50 border border-amber-200 rounded-xl p-4"
              >
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-900 mb-1">
                      Guardian Approval Required
                    </p>
                    <p className="text-xs text-amber-700">
                      Students under 13 require a parent or guardian to approve their account before
                      they can access the platform. Please provide your guardian's email address.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

          {/* Teen (13-17) Info Banner - optional guardian */}
          {isTeen &&
            !(
              invitationData?.organization?.type === 'family' && invitationData?.invitedBy?.email
            ) && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-blue-50 border border-blue-200 rounded-xl p-4"
              >
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900 mb-1">
                      Optional: Invite a Guardian
                    </p>
                    <p className="text-xs text-blue-700">
                      You can optionally invite a parent or guardian to help manage your account.
                      They'll be able to view your progress and manage your family subscription. You
                      can skip this now and invite a guardian later from your profile.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
        </AnimatePresence>

        {/* Social Authentication */}
        {/* {!invitationToken && !showMinorFlow && (
          <SocialAuthButtons loading={isLoading} disabled={isLoading} />
        )} */}

        {/* Name Fields */}
        <div className="grid grid-cols-2 gap-4">
          <FormInput
            id="firstName"
            name="firstName"
            type="text"
            label="First Name"
            value={formData.firstName}
            onChange={handleChange}
            error={errors.firstName}
            required
            autoComplete="given-name"
            icon={<User className="w-5 h-5" />}
            disabled={
              isLoading || loadingInvitation || (invitationData?.metadata?.firstName ? true : false)
            }
          />
          <FormInput
            id="lastName"
            name="lastName"
            type="text"
            label="Last Name"
            value={formData.lastName}
            onChange={handleChange}
            error={errors.lastName}
            required
            autoComplete="family-name"
            disabled={
              isLoading || loadingInvitation || (invitationData?.metadata?.lastName ? true : false)
            }
          />
        </div>

        {/* Email Input */}
        <FormInput
          id="email"
          name="email"
          type="email"
          label="Email Address"
          value={formData.email}
          onChange={handleChange}
          error={errors.email}
          required
          autoComplete="email"
          icon={<Mail className="w-5 h-5" />}
          disabled={isLoading || loadingInvitation || !!invitationData}
          readOnly={!!invitationData}
          success={!!invitationData}
        />

        {/* Date of Birth Input */}
        <div className="space-y-2">
          <FormInput
            id="dateOfBirth"
            name="dateOfBirth"
            type="date"
            label="Date of Birth"
            value={formData.dateOfBirth}
            onChange={handleChange}
            error={errors.dateOfBirth}
            required
            icon={<Calendar className="w-5 h-5" />}
            disabled={isLoading || loadingInvitation}
            max={new Date().toISOString().split('T')[0]}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
            We collect your date of birth to comply with child safety regulations (COPPA) and to
            ensure appropriate content and parental consent requirements for users under 18. This
            information is securely stored and never shared with third parties.{' '}
            <a
              href="/legal/privacy"
              className="text-primary-600 dark:text-primary-400 hover:underline"
            >
              Learn more
            </a>
          </p>
        </div>

        {/* Guardian Email (only for minors without invitation from family guardian) */}
        <AnimatePresence>
          {showMinorFlow &&
            !(
              invitationData?.organization?.type === 'family' && invitationData?.invitedBy?.email
            ) && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-3"
              >
                {/* Guardian email input - required for under 13, required for teens unless skipped */}
                <FormInput
                  id="guardianEmail"
                  name="guardianEmail"
                  type="email"
                  label={
                    isUnder13
                      ? "Guardian's Email Address"
                      : isTeen && skipGuardian
                        ? "Guardian's Email Address (Skipped)"
                        : "Guardian's Email Address"
                  }
                  value={formData.guardianEmail}
                  onChange={handleChange}
                  error={errors.guardianEmail}
                  required={isUnder13 || (isTeen && !skipGuardian)}
                  autoComplete="off"
                  icon={<Mail className="w-5 h-5" />}
                  disabled={isLoading || loadingInvitation || (isTeen && skipGuardian)}
                />

                {/* Skip guardian checkbox for teens (13-17) */}
                {isTeen && (
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={skipGuardian}
                      onChange={(e) => {
                        setSkipGuardian(e.target.checked)
                        if (e.target.checked) {
                          setFormData((prev) => ({ ...prev, guardianEmail: '' }))
                          setErrors((prev) => ({ ...prev, guardianEmail: '' }))
                        }
                      }}
                      disabled={isLoading || loadingInvitation}
                      className="mt-0.5 w-4 h-4 text-primary-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    />
                    <div className="flex-1 select-none">
                      <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                        I don't want to add a guardian right now
                      </span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        You can invite a guardian anytime from your profile settings
                      </span>
                    </div>
                  </label>
                )}
              </motion.div>
            )}
        </AnimatePresence>

        {/* Password Fields */}
        <PasswordInput
          id="password"
          name="password"
          label="Password"
          value={formData.password}
          onChange={handleChange}
          error={errors.password}
          required
          autoComplete="new-password"
          disabled={isLoading || loadingInvitation}
          showStrengthMeter={true}
        />

        <PasswordInput
          id="confirmPassword"
          name="confirmPassword"
          label="Confirm Password"
          value={formData.confirmPassword}
          onChange={handleChange}
          error={errors.confirmPassword}
          required
          autoComplete="new-password"
          disabled={isLoading || loadingInvitation}
          showStrengthMeter={false}
        />

        {/* Age Confirmation, Terms and Privacy (required for everyone) */}
        <div className="space-y-4 pt-2 border-t border-gray-200 dark:border-gray-700">
          {/* Age Confirmation */}
          <div className="space-y-2">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                name="ageConfirmed"
                checked={formData.ageConfirmed}
                onChange={handleCheckboxChange}
                disabled={isLoading || loadingInvitation}
                className="mt-0.5 w-4 h-4 text-primary-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              />
              <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 select-none group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                I confirm that I am 13 years of age or older
              </span>
            </label>
            <AnimatePresence>
              {errors.ageConfirmed && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center gap-1.5 text-xs text-error-600 dark:text-error-400 pl-7"
                >
                  <AlertCircle className="w-3 h-3" />
                  <span>{errors.ageConfirmed}</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Terms Acceptance */}
          <div className="space-y-2">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                name="termsAccepted"
                checked={formData.termsAccepted}
                onChange={handleCheckboxChange}
                disabled={isLoading || loadingInvitation}
                className="mt-0.5 w-4 h-4 text-primary-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              />
              <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 select-none group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                I accept the{' '}
                <Link
                  to="/legal/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium underline underline-offset-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms of Service
                </Link>
              </span>
            </label>
            <AnimatePresence>
              {errors.termsAccepted && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center gap-1.5 text-xs text-error-600 dark:text-error-400 pl-7"
                >
                  <AlertCircle className="w-3 h-3" />
                  <span>{errors.termsAccepted}</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Privacy Acceptance */}
          <div className="space-y-2">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                name="privacyAccepted"
                checked={formData.privacyAccepted}
                onChange={handleCheckboxChange}
                disabled={isLoading || loadingInvitation}
                className="mt-0.5 w-4 h-4 text-primary-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              />
              <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 select-none group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                I accept the{' '}
                <Link
                  to="/legal/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium underline underline-offset-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </Link>
              </span>
            </label>
            <AnimatePresence>
              {errors.privacyAccepted && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center gap-1.5 text-xs text-error-600 dark:text-error-400 pl-7"
                >
                  <AlertCircle className="w-3 h-3" />
                  <span>{errors.privacyAccepted}</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={
            isLoading ||
            loadingInvitation ||
            !formData.ageConfirmed ||
            !formData.termsAccepted ||
            !formData.privacyAccepted
          }
          whileHover={{
            scale:
              isLoading ||
              loadingInvitation ||
              !formData.ageConfirmed ||
              !formData.termsAccepted ||
              !formData.privacyAccepted
                ? 1
                : 1.01
          }}
          whileTap={{
            scale:
              isLoading ||
              loadingInvitation ||
              !formData.ageConfirmed ||
              !formData.termsAccepted ||
              !formData.privacyAccepted
                ? 1
                : 0.99
          }}
          className="w-full flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              <span>
                {isUnder13 &&
                !(
                  invitationData?.organization?.type === 'family' &&
                  invitationData?.invitedBy?.email
                )
                  ? 'Sending approval request...'
                  : 'Creating account...'}
              </span>
            </>
          ) : (
            <span>
              {isUnder13 &&
              !(invitationData?.organization?.type === 'family' && invitationData?.invitedBy?.email)
                ? 'Request Guardian Approval'
                : isTeen && !skipGuardian && formData.guardianEmail.trim()
                  ? 'Create Account & Invite Guardian'
                  : isTeen
                    ? 'Create Account'
                    : isMinor
                      ? 'Complete Registration'
                      : 'Create Student Account'}
            </span>
          )}
        </motion.button>

        {/* Sign In Link */}
        <div className="text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
            >
              Sign in
            </Link>
          </p>
        </div>
      </form>
    </AuthLayout>
  )
}

export default RegisterStudent
